import React, { Suspense } from 'react'
import { Switch, Route, NotFound, AuthRoute, Redirect } from '@fs/zion-router'
import { NoticeLoading } from '@fs/zion-icon'
import ErrorBoundary from '@fs/zion-error-boundary'
import GlobalState from './context/globalState'
import ApplicationsGate from './components/ApplicationsGate'
import useHasAdminPermissions from './hooks/useHasAdminPermissions'

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy

const OnboardingSteps = React.lazy(() => import('./components/Onboarding/OnboardingSteps'))
const ApplicationsOverview = React.lazy(() => import('./components/ApplicationsOverview'))
const ApplicationEnvironments = React.lazy(() => import('./components/ApplicationEnvironments/ApplicationEnvironments'))
const ApplicationDetails = React.lazy(() => import('./components/ApplicationDetails'))
const DeveloperProfile = React.lazy(() => import('./components/DeveloperProfile'))
const AdminDashboard = React.lazy(() => import('./components/AdminDashboard/AdminDashboard'))
const DeveloperDetails = React.lazy(() => import('./components/AdminDashboard/DeveloperDetails'))
const Migration = React.lazy(() => import('./components/AdminDashboard/Migration'))

function App(): JSX.Element {
  return (
    <GlobalState>
      <ErrorBoundary>
        {/* <Header /> */}
        <Switch>
          <AuthRoute exact path="/">
            <ApplicationsGate>
              <ApplicationsOverview />
            </ApplicationsGate>
          </AuthRoute>
          <AuthRoute exact path="/environments/:id">
            <ApplicationsGate>
              <ApplicationEnvironments />
            </ApplicationsGate>
          </AuthRoute>
          <AuthRoute exact path="/details/:id">
            <ApplicationsGate>
              <ApplicationDetails />
            </ApplicationsGate>
          </AuthRoute>
          <AuthRoute exact path="/onboarding" component={OnboardingSteps} />
          <AuthRoute exact path="/profile" component={DeveloperProfile} />
          <AuthRoute exact path="/migration" component={Migration} />
          <AuthRoute exact path="/admin">
            <AdminGate>
              <AdminDashboard />
            </AdminGate>
          </AuthRoute>
          <AuthRoute exact path="/admin/:id">
            <AdminGate>
              <DeveloperDetails />
            </AdminGate>
          </AuthRoute>

          {/* blank page for performance testing purposes only */}
          <Route path="/blank" component={() => 'This page is intentionally left blank'} />

          <Route component={NotFound} />
        </Switch>
      </ErrorBoundary>
    </GlobalState>
  )
}

/**
 * Gates an already-matched admin route on the caller's admin permission. This is
 * deliberately not a <Route> itself — a nested route inside a pathless <Switch>
 * child would let the first admin entry swallow every location, blanking any
 * path it did not itself match.
 */
function AdminGate({ children }: { children: React.ReactNode }): JSX.Element {
  const { hasPermission, status } = useHasAdminPermissions()
  if (status === 'loading') {
    return <NoticeLoading />
  }
  if (!hasPermission) {
    return <Redirect to="/" />
  }
  return <Suspense fallback={<NoticeLoading />}>{children}</Suspense>
}
export default App
